import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CapitalAmortization, CapitalAmortizationUpdate, DestinoAmortizacion, EstadoAmortizacion } from '../../types/CapitalAmortization';
import { formatCurrency } from '../../utils/format';
import DateInput from '../common/DateInput';
import AmountInput from './AmountInput';

interface AmortizacionCardProps {
  amortizacion: CapitalAmortization;
  busy: boolean;
  onSave: (id: string, data: CapitalAmortizationUpdate) => Promise<void>;
  onEstado: (id: string, estado: EstadoAmortizacion) => void;
  onEjecutar: (a: CapitalAmortization) => void;
  onAnular: (a: CapitalAmortization) => void;
}

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const AmortizacionCard: React.FC<AmortizacionCardProps> = ({ amortizacion: a, busy, onSave, onEstado, onEjecutar, onAnular }) => {
  const editable = a.estado !== 'Ejecutada';
  const [capitales, setCapitales] = useState<Record<string, number>>({});
  const [destino, setDestino] = useState<DestinoAmortizacion>(a.destino);
  const [destinoDetalle, setDestinoDetalle] = useState(a.destinoDetalle ?? '');
  const [fechaCorte, setFechaCorte] = useState(a.fechaCorte);
  const [notas, setNotas] = useState(a.notas ?? '');
  const [showDetalle, setShowDetalle] = useState(false);

  // Al recibir la amortización recalculada del servidor se descartan los cambios locales
  useEffect(() => {
    setCapitales(Object.fromEntries(a.lines.map(l => [l.id, l.capitalAmortizado])));
    setDestino(a.destino);
    setDestinoDetalle(a.destinoDetalle ?? '');
    setFechaCorte(a.fechaCorte);
    setNotas(a.notas ?? '');
  }, [a]);

  const dirty = useMemo(() =>
    a.lines.some(l => (capitales[l.id] ?? 0) !== l.capitalAmortizado)
    || destino !== a.destino
    || destinoDetalle !== (a.destinoDetalle ?? '')
    || fechaCorte !== a.fechaCorte
    || notas !== (a.notas ?? ''),
  [a, capitales, destino, destinoDetalle, fechaCorte, notas]);

  const invalid = a.lines.some(l => {
    const v = capitales[l.id];
    return v === undefined || v < 0 || v > l.capitalPrevio;
  });

  const save = () => onSave(a.id, {
    fechaCorte,
    fechaDevolucionRef: a.fechaDevolucionRef ?? null,
    destino,
    destinoDetalle: destinoDetalle.trim() || null,
    notas: notas.trim() || null,
    lines: a.lines.map(l => ({ id: l.id, capitalAmortizado: capitales[l.id] ?? 0 })),
  });

  const setCapital = (lineId: string, value: number | undefined) =>
    setCapitales(prev => ({ ...prev, [lineId]: value ?? 0 }));

  return (
    <div className={`amz-card amz-card-${a.estado.toLowerCase()}`}>
      <div className="amz-card-header">
        <div className="amz-card-title">
          <Link to={`/usuarios/${a.investorId}`} className="amz-investor">{a.investorName}</Link>
          <span className={`amz-badge amz-badge-${a.estado.toLowerCase()}`}>{a.estado}</span>
          {a.fechaEjecucion && <span className="amz-muted">Ejecutada el {fmtDate(a.fechaEjecucion)}</span>}
        </div>
        <div className="amz-card-totals">
          <span>Amortiza <strong>{formatCurrency(a.capitalAmortizado)}</strong></span>
          <span>Mantiene <strong>{formatCurrency(a.capitalRestante)}</strong></span>
          <span>Neto a pagar <strong>{formatCurrency(a.neto)}</strong></span>
        </div>
      </div>

      <p className="amz-resumen">{a.resumen}</p>

      <div className="amz-card-controls">
        <div className="amz-field">
          <label>Destino del capital</label>
          <select value={destino} disabled={!editable} onChange={e => setDestino(e.target.value as DestinoAmortizacion)}>
            <option value="DEVOLUCION">Devolución al inversor</option>
            <option value="REINVERSION">Reinversión en otro producto</option>
          </select>
        </div>
        {destino === 'REINVERSION' && (
          <div className="amz-field">
            <label>Producto de reinversión</label>
            <input type="text" value={destinoDetalle} disabled={!editable} maxLength={200}
                   onChange={e => setDestinoDetalle(e.target.value)} />
          </div>
        )}
        <div className="amz-field">
          <label>Fecha de corte</label>
          <DateInput value={fechaCorte} disabled={!editable} onChange={e => setFechaCorte(e.target.value)} />
        </div>
        <div className="amz-field amz-field-grow">
          <label>Notas</label>
          <input type="text" value={notas} disabled={!editable} onChange={e => setNotas(e.target.value)} />
        </div>
      </div>

      <div className="amz-table-wrap">
        <table className="amz-table">
          <thead>
            <tr>
              <th>Fase / ficha</th>
              <th className="num">Capital vivo</th>
              <th className="num">A amortizar</th>
              <th className="num">Restante</th>
              <th className="num" title="Rendimiento total × proporción de días × capital amortizado">Intereses tramo</th>
              <th className="num" title="Intereses a cuenta ya liquidados, en proporción al capital amortizado">A cuenta pagados</th>
              <th className="num" title="Fidelización del tramo pendiente de pagar">Fidelización</th>
              <th className="num">Bruto</th>
              <th className="num">IRPF</th>
              <th className="num">Neto</th>
            </tr>
          </thead>
          <tbody>
            {a.lines.map(l => {
              const value = capitales[l.id] ?? 0;
              const outOfRange = value < 0 || value > l.capitalPrevio;
              return (
                <React.Fragment key={l.id}>
                  <tr>
                    <td>
                      <div>{l.phaseName}</div>
                      <div className="amz-muted">{l.investmentSheetId}</div>
                    </td>
                    <td className="num">{formatCurrency(l.capitalPrevio)}</td>
                    <td className="num">
                      {editable ? (
                        <div className="amz-amount-cell">
                          <AmountInput value={value} onChange={v => setCapital(l.id, v)}
                                       className={outOfRange ? 'amz-input-error' : ''} />
                          <div className="amz-quick">
                            <button type="button" onClick={() => setCapital(l.id, 0)}>Nada</button>
                            <button type="button" onClick={() => setCapital(l.id, l.capitalPrevio)}>Todo</button>
                          </div>
                        </div>
                      ) : formatCurrency(l.capitalAmortizado)}
                    </td>
                    <td className="num">{formatCurrency(l.capitalPrevio - value)}</td>
                    <td className="num">{formatCurrency(l.interesTramo)}</td>
                    <td className="num">−{formatCurrency(l.interesesCuentaImputados)}</td>
                    <td className="num">{formatCurrency(l.fidelizacionTramo - l.fidelizacionImputada)}</td>
                    <td className="num">{formatCurrency(l.pendienteBruto)}</td>
                    <td className="num">−{formatCurrency(l.retencionIrpf)}</td>
                    <td className="num"><strong>{formatCurrency(l.neto)}</strong></td>
                  </tr>
                  {showDetalle && (
                    <tr className="amz-detail-row">
                      <td colSpan={10}>
                        Inicio de pago {fmtDate(l.fechaInicioPago)} · devolución de referencia {fmtDate(l.fechaDevolucionRef)} ·
                        {' '}{l.diasTranscurridos} de {l.diasTotales} días · rendimiento total {l.porcentajeRtoTotal}% ·
                        {' '}a cuenta {l.porcentajeInteresCuenta}% anual · fidelización {l.porcentajeFidelizacion ?? 0}% ·
                        {' '}IRPF {l.porcentajeRetencionIrpf}%{l.ibanDevolucion ? ` · IBAN ${l.ibanDevolucion}` : ''}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {a.lines.length > 1 && (
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{formatCurrency(a.capitalPrevio)}</td>
                <td className="num">{formatCurrency(a.capitalAmortizado)}</td>
                <td className="num">{formatCurrency(a.capitalRestante)}</td>
                <td colSpan={3} />
                <td className="num">{formatCurrency(a.pendienteBruto)}</td>
                <td className="num">−{formatCurrency(a.retencionIrpf)}</td>
                <td className="num"><strong>{formatCurrency(a.neto)}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {dirty && <p className="amz-warning">Cambios sin guardar: los intereses se recalculan al guardar.</p>}

      <div className="amz-card-actions">
        <button type="button" className="amz-link" onClick={() => setShowDetalle(s => !s)}>
          {showDetalle ? 'Ocultar detalle del cálculo' : 'Ver detalle del cálculo'}
        </button>
        <div className="amz-card-buttons">
          {editable && (
            <button type="button" className="amz-btn amz-btn-primary" disabled={busy || !dirty || invalid} onClick={save}>
              Guardar y recalcular
            </button>
          )}
          {a.estado === 'Propuesta' && (
            <>
              <button type="button" className="amz-btn amz-btn-secondary" disabled={busy || dirty} onClick={() => onEstado(a.id, 'Descartada')}>
                Descartar
              </button>
              <button type="button" className="amz-btn amz-btn-success" disabled={busy || dirty} onClick={() => onEstado(a.id, 'Acordada')}>
                Marcar acordada
              </button>
            </>
          )}
          {a.estado === 'Acordada' && (
            <>
              <button type="button" className="amz-btn amz-btn-secondary" disabled={busy || dirty} onClick={() => onEstado(a.id, 'Propuesta')}>
                Volver a propuesta
              </button>
              <button type="button" className="amz-btn amz-btn-danger" disabled={busy || dirty} onClick={() => onEjecutar(a)}>
                Ejecutar
              </button>
            </>
          )}
          {a.estado === 'Descartada' && (
            <button type="button" className="amz-btn amz-btn-secondary" disabled={busy} onClick={() => onEstado(a.id, 'Propuesta')}>
              Recuperar
            </button>
          )}
          {a.estado === 'Ejecutada' && (
            <button type="button" className="amz-btn amz-btn-secondary" disabled={busy} onClick={() => onAnular(a)}>
              Anular ejecución
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmortizacionCard;
